import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, KeyRound, Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { useResetPassword } from "@/lib/hooks/auth";

/**
 * /password/reset/$token — completa il reset password (public, no auth guard).
 *
 * Il token (dal link email) è l'autenticazione. Se l'account ha la TOTP
 * enrollata, il BE richiede anche un codice OTP (campo opzionale qui: il server
 * impone se necessario e ritorna invalid_credentials se mancante/errato).
 */
const FormSchema = z
  .object({
    new_password: z.string().min(12, "validation.password_min").max(128, "validation.password_max"),
    confirm_password: z.string(),
    otp_code: z.string().optional(),
  })
  .refine((v) => v.new_password === v.confirm_password, {
    message: "validation.password_mismatch",
    path: ["confirm_password"],
  })
  .refine((v) => !v.otp_code || /^\d{6,8}$/.test(v.otp_code), {
    message: "validation.mfa_code",
    path: ["otp_code"],
  });

type FormValues = z.infer<typeof FormSchema>;

export const Route = createFileRoute("/password/reset/$token")({
  component: ResetPasswordPage,
});

function ResetPasswordPage(): JSX.Element {
  const { t } = useTranslation();
  const { token } = Route.useParams();
  const reset = useResetPassword();
  const [done, setDone] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { new_password: "", confirm_password: "", otp_code: "" },
    mode: "onBlur",
  });

  async function onSubmit(values: FormValues): Promise<void> {
    try {
      await reset.mutateAsync({
        token,
        new_password: values.new_password,
        otp_code: values.otp_code ? values.otp_code : undefined,
      });
      setDone(true);
    } catch {
      // surfaced via reset.error block
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-muted/30 p-6">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-2 pb-2">
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="size-5 text-primary" aria-hidden />
            {t("reset.title")}
          </CardTitle>
          <CardDescription>{t("reset.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {done ? (
            <div className="rounded-md border border-success/40 bg-success/5 p-3 text-sm text-success">
              <div className="flex items-center gap-2 font-medium">
                <CheckCircle2 className="size-4" aria-hidden />
                {t("reset.success_title")}
              </div>
              <p className="mt-1 text-xs">{t("reset.success_hint")}</p>
              <Link to="/system" className="mt-2 inline-block text-xs text-primary underline">
                {t("reset.go_to_login")}
              </Link>
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                void form.handleSubmit(onSubmit)(e);
              }}
              className="space-y-3"
              noValidate
            >
              <div className="space-y-1">
                <Label htmlFor="new_password" className="text-xs font-medium">
                  {t("reset.new_password")}
                </Label>
                <Input
                  id="new_password"
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  disabled={reset.isPending}
                  {...form.register("new_password")}
                />
                <p className="text-xs text-muted-foreground">{t("reset.password_hint")}</p>
                {form.formState.errors.new_password?.message && (
                  <p className="text-xs text-destructive" role="alert">
                    {t(String(form.formState.errors.new_password.message))}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="confirm_password" className="text-xs font-medium">
                  {t("reset.confirm_password")}
                </Label>
                <Input
                  id="confirm_password"
                  type="password"
                  autoComplete="new-password"
                  disabled={reset.isPending}
                  {...form.register("confirm_password")}
                />
                {form.formState.errors.confirm_password?.message && (
                  <p className="text-xs text-destructive" role="alert">
                    {t(String(form.formState.errors.confirm_password.message))}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="otp_code" className="text-xs font-medium">
                  {t("reset.otp_label")}
                </Label>
                <Input
                  id="otp_code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  disabled={reset.isPending}
                  {...form.register("otp_code")}
                />
                <p className="text-xs text-muted-foreground">{t("reset.otp_hint")}</p>
                {form.formState.errors.otp_code?.message && (
                  <p className="text-xs text-destructive" role="alert">
                    {t(String(form.formState.errors.otp_code.message))}
                  </p>
                )}
              </div>

              {reset.isError && (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
                  <ul className="ml-4 list-disc space-y-0.5">
                    {formatApiError(reset.error).map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={reset.isPending}>
                {reset.isPending && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />}
                {t("reset.submit")}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
